import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { alerts as alertsApi, integrationsApi } from "@/lib/api";
import type { Alert, AlertEvidence, FieldChange } from "@/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/icon";
import { AlertSummary } from "@/components/alerts/AlertSummary";
import { ChangeExcerpt } from "@/components/alerts/ChangeExcerpt";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { BrandLogo } from "@/components/settings/SettingsLayout";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime, pageTypeLabel, timeAgo } from "@/lib/utils";
import { cn } from "@/lib/utils";

const SIGNAL_COLORS = {
  high: { dot: "bg-red-500", text: "text-red-400", label: "High signal" },
  medium: { dot: "bg-amber-500", text: "text-amber-400", label: "Medium signal" },
  low: { dot: "bg-zinc-400", text: "text-zinc-400", label: "Low signal" },
};

export function AlertDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const canExportJson = workspace?.plan === "growth";
  const [alert, setAlert] = useState<Alert | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [feedback, setFeedback] = useState<"useful" | "not_useful" | null>(null);
  const [evidence, setEvidence] = useState<AlertEvidence | null>(null);
  // Linear: null = not connected; otherwise the issue filed for this alert, if any
  const [linear, setLinear] = useState<{ issue: { identifier: string; url: string } | null } | null>(null);
  const [isFiling, setIsFiling] = useState(false);

  useEffect(() => {
    if (!id) return;
    void integrationsApi.list().then(async (res) => {
      if (!("data" in res) || !res.data?.linear.connected) return;
      const issue = await integrationsApi.linearIssueFor(id);
      setLinear({ issue: "data" in issue ? issue.data ?? null : null });
    });
  }, [id]);

  const fileLinearIssue = async () => {
    if (!id) return;
    setIsFiling(true);
    const res = await integrationsApi.createLinearIssue(id);
    if ("data" in res && res.data) {
      setLinear({ issue: res.data });
      toast.success(`Created ${res.data.identifier} in Linear`);
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setIsFiling(false);
  };

  useEffect(() => {
    if (!id) return;
    async function load() {
      const res = await alertsApi.get(id!);
      if ("data" in res && res.data) {
        setAlert(res.data);
        setFeedback(res.data.feedback ?? null);
        if (res.data.status === "unread") alertsApi.markRead(id!);
        if (res.data.hasEvidence) {
          const ev = await alertsApi.evidence(id!);
          if ("data" in ev && ev.data) setEvidence(ev.data);
        }
      }
      setIsLoading(false);
    }
    load();
  }, [id]);

  const handleFeedback = async (value: "useful" | "not_useful") => {
    if (!id) return;
    setFeedback(value);
    await alertsApi.feedback(id, value);
  };

  const handleArchive = async () => {
    if (!id) return;
    const res = await alertsApi.archive(id);
    if ("data" in res) {
      toast.success("Alert archived");
      navigate("/alerts");
    }
  };

  const getAlertJson = () => JSON.stringify({
    exportedAt: new Date().toISOString(),
    alert: { ...alert, feedback },
    evidence,
  }, null, 2);

  const copyAlertJson = async () => {
    try {
      await navigator.clipboard.writeText(getAlertJson());
      toast.success("Alert JSON copied");
    } catch {
      toast.error("Couldn't copy alert JSON");
    }
  };

  const downloadAlertJson = () => {
    if (!alert) return;
    const blob = new Blob([getAlertJson()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `alert-${alert.id}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success("Alert JSON downloaded");
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-14 items-center border-b border-border px-6 gap-3">
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="w-full">
            <div className="grid gap-px xl:grid-cols-12">
              <div className="space-y-6 rounded-lg bg-card p-5 sm:p-6 xl:col-span-8">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <Skeleton className="h-3 w-64" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <div className="space-y-3">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
                <div className="border-t border-border pt-5">
                  <Skeleton className="mb-3 h-3 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-3/4" />
                </div>
              </div>
              <div className="space-y-6 rounded-lg bg-card p-5 sm:p-6 xl:col-span-4">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-16 w-full" />
                <div className="grid grid-cols-2 gap-px bg-border">
                  <Skeleton className="h-16 rounded-none" />
                  <Skeleton className="h-16 rounded-none" />
                  <Skeleton className="col-span-2 h-12 rounded-none" />
                </div>
              </div>
            </div>
            <div className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
              <Skeleton className="mb-4 h-3 w-24" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="mt-1 h-10 w-full" />
              <Skeleton className="mt-1 h-10 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Alert not found" />
        <div className="flex flex-col items-center gap-3 py-20 text-center">
          <Icon name="alert-01" size={28} className="text-muted-foreground/20" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Alert not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/alerts")}>Back to alerts</Button>
        </div>
      </div>
    );
  }

  const signal = SIGNAL_COLORS[alert.signalScore] ?? SIGNAL_COLORS.low;
  const linearAction = linear && (linear.issue ? (
    <a
      href={linear.issue.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
    >
      <BrandLogo domain="linear.app" name="Linear" fallbackIcon="flow-connection" className="!h-4 !w-4 rounded-sm border-0 bg-transparent" />
      {linear.issue.identifier}
    </a>
  ) : (
    <button
      onClick={() => void fileLinearIssue()}
      disabled={isFiling}
      className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
    >
      {isFiling ? <Icon name="refresh" size={10} className="animate-spin" /> : <BrandLogo domain="linear.app" name="Linear" fallbackIcon="flow-connection" className="!h-4 !w-4 rounded-sm border-0 bg-transparent" />}
      Create Linear issue
    </button>
  ));

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={`${pageTypeLabel(alert.pageType)} change`}
        actions={
          <div className="flex items-center gap-2">
            {canExportJson && (
              <>
                <Button variant="ghost" size="icon-sm" onClick={() => void copyAlertJson()} title="Copy alert as JSON" aria-label="Copy alert as JSON">
                  <Icon name="copy-01" size={13} />
                </Button>
                <Button variant="ghost" size="icon-sm" onClick={downloadAlertJson} title="Download alert as JSON" aria-label="Download alert as JSON">
                  <Icon name="download-01" size={13} />
                </Button>
              </>
            )}
            <button
              onClick={() => navigate("/alerts")}
              className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="arrow-left-01" size={11} />Back
            </button>
            <button
              onClick={handleArchive}
              className="font-mono text-[11px] text-muted-foreground hover:text-destructive transition-colors"
            >
              Archive
            </button>
          </div>
        }
      />

        <div className="flex-1 overflow-y-auto p-6">
          <div className="w-full">
            <div className="grid gap-px xl:grid-cols-12">
              <section className="space-y-6 rounded-lg bg-card p-5 sm:p-6 xl:col-span-8">

                {/* â”€â”€ Meta row â”€â”€ */}
                <div className="flex flex-wrap items-center gap-3 border-b border-border pb-4">
                  <div className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", signal.dot)} />
                  <span className={cn("font-mono text-[11px] uppercase tracking-widest", signal.text)}>
                    {signal.label}
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">·</span>
                  <span className="font-mono text-[11px] text-muted-foreground">{pageTypeLabel(alert.pageType)}</span>
                  {alert.competitor && (
                    <>
                      <span className="font-mono text-[11px] text-muted-foreground">·</span>
                      <div className="flex items-center gap-1.5">
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${alert.competitor.domain}&sz=16`}
                          alt="" className="h-3 w-3 rounded"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                        <span className="font-mono text-[11px] uppercase tracking-widest text-foreground">
                          {alert.competitor.name}
                        </span>
                      </div>
                    </>
                  )}
                  <span className="font-mono text-[11px] text-muted-foreground sm:ml-auto">{timeAgo(alert.detectedAt)}</span>
                </div>

                {/* â”€â”€ Summary â”€â”€ */}
                <div className="space-y-4">
                  <div className="border-b border-border pb-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Alert briefing</p>
                    <h2 className="mt-1 text-base font-medium tracking-tight text-foreground">What changed</h2>
                  </div>
                  <AlertSummary summary={alert.summary} className="max-w-4xl font-sans text-[13px] leading-6 text-foreground/80" />
                </div>

                {/* â”€â”€ Why this score â”€â”€ */}
                {alert.reasoning && (
                  <div className="border-t border-border pt-5">
                    <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                      Why {signal.label.toLowerCase()}
                    </p>
                    <p className="font-mono text-[11px] leading-relaxed text-foreground/70">{alert.reasoning}</p>
                  </div>
                )}

                {/* â”€â”€ Feedback â”€â”€ */}
                <div className="flex flex-wrap items-center gap-3 border-t border-border pt-5">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Was this useful?</p>
                  <button
                    onClick={() => handleFeedback("useful")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-mono text-[11px] transition-colors",
                      feedback === "useful"
                        ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400"
                        : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                    )}
                  >
                    <Icon name="thumbs-up" size={12} />
                    Useful
                  </button>
                  <button
                    onClick={() => handleFeedback("not_useful")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-mono text-[11px] transition-colors",
                      feedback === "not_useful"
                        ? "border-red-500/50 bg-red-500/10 text-red-400"
                        : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                    )}
                  >
                    <Icon name="thumbs-down" size={12} />
                    Not useful
                  </button>
                  {feedback && (
                    <span className="ml-1 font-mono text-[11px] text-muted-foreground">Thanks for the feedback</span>
                  )}
                </div>
              </section>

              <aside className="space-y-6 rounded-lg bg-card p-5 sm:p-6 xl:col-span-4">
                {/* â”€â”€ Source â”€â”€ */}
                {alert.watchedUrl && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Source</p>
                      <a
                        href={alert.watchedUrl.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex shrink-0 items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <Icon name="link-01" size={10} />
                        Open page
                      </a>
                    </div>
                    <div className="flex items-start gap-3 rounded-lg border border-border px-4 py-3">
                      <Icon name="link-01" size={12} className="mt-0.5 shrink-0 text-muted-foreground" />
                      <a
                        href={alert.watchedUrl.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 flex-1 break-words font-sans text-xs text-foreground underline-offset-2 hover:underline"
                      >
                        {alert.watchedUrl.url}
                      </a>
                    </div>
                    <p className="font-mono text-[11px] text-muted-foreground">{alert.watchedUrl.label}</p>
                  </div>
                )}

                {/* â”€â”€ Metadata â”€â”€ */}
                <div className="grid grid-cols-2 gap-px border border-border bg-border">
                  <div className="bg-card p-3">
                    <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Detected</p>
                    <p className="font-mono text-[11px] text-foreground">{formatDateTime(alert.detectedAt)}</p>
                  </div>
                  <div className="bg-card p-3">
                    <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</p>
                    <p className="font-mono text-[11px] capitalize text-foreground">{alert.status}</p>
                  </div>
                  {alert.deliveredVia?.length > 0 && (
                    <div className="col-span-2 bg-card p-3">
                      <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Delivered via</p>
                      <p className="font-mono text-[11px] text-foreground">{alert.deliveredVia.join(", ")}</p>
                    </div>
                  )}
                </div>
              </aside>
            </div>

            {/* â”€â”€ Evidence: the two snapshots and everything that differs between them â”€â”€ */}
            {evidence?.available && <div className="mt-6"><EvidencePanel evidence={evidence} /></div>}

            {/* â”€â”€ Change excerpt (fallback when the snapshots have been rotated out) â”€â”€ */}
            {!evidence?.available && alert.changeExcerpt && (
              <div className="mt-6">
                <ChangeExcerpt excerpt={alert.changeExcerpt} action={linearAction} />
              </div>
            )}
          </div>
      </div>
    </div>
  );
}
// â”€â”€â”€ Evidence â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CATEGORY_LABELS: Record<FieldChange["category"], string> = {
  pricing: "Pricing",
  seo: "SEO & meta tags",
  social: "Social cards",
  structure: "Headings",
  navigation: "Navigation",
  cta: "Calls to action",
  schema: "Structured data",
  tech: "Tools & technology",
  page: "Page",
};

const CATEGORY_ORDER: FieldChange["category"][] = [
  "pricing", "navigation", "cta", "structure", "seo", "social", "schema", "tech", "page",
];

function EvidencePanel({ evidence }: { evidence: AlertEvidence }) {
  const [showAllLines, setShowAllLines] = useState(false);
  const fields = evidence.fieldChanges ?? [];
  const lines = [
    ...(evidence.removed ?? []).map((text) => ({ kind: "removed" as const, text })),
    ...(evidence.added ?? []).map((text) => ({ kind: "added" as const, text })),
  ];
  const visibleLines = showAllLines ? lines : lines.slice(0, 12);
  const fieldRows = fields.map((change, index) => ({ ...change, id: `${change.category}-${change.field}-${index}` }));
  const fieldColumns: DataTableColumn<(typeof fieldRows)[number]>[] = [
    {
      id: "field",
      header: "Field",
      width: "16rem",
      cell: (row) => (
        <div>
          <p className="font-mono text-[11px] text-foreground">{row.field}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{CATEGORY_LABELS[row.category]}</p>
        </div>
      ),
    },
    {
      id: "change",
      header: "Change",
      cell: (row) => (
        <div className="grid gap-2 md:grid-cols-2">
          <span className="break-words font-mono text-[11px] text-red-500/80">{row.kind === "added" ? "Not present" : row.before}</span>
          <span className="break-words font-mono text-[11px] text-emerald-600">{row.kind === "removed" ? "Removed" : row.after}</span>
        </div>
      ),
    },
  ];
  const lineColumns: DataTableColumn<(typeof lines)[number] & { id: string }>[] = [
    {
      id: "type",
      header: "Type",
      width: "8rem",
      cell: (row) => (
        <span className={cn("inline-flex items-center gap-2", row.kind === "removed" ? "text-red-500" : "text-emerald-600")} title={row.kind}>
          <Icon name={row.kind === "removed" ? "arrow-down-01" : "arrow-up-01"} size={13} aria-hidden="true" />
          <span className="sr-only">{row.kind}</span>
        </span>
      ),
    },
    {
      id: "line",
      header: "Changed content",
      cell: (row) => <span className="break-words font-mono text-[11px] leading-relaxed text-foreground/70">{row.text}</span>,
    },
  ];
  const lineRows = visibleLines.map((line, index) => ({ ...line, id: `${line.kind}-${index}` }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Evidence</p>
        {evidence.previous && evidence.current && (
          <p className="font-mono text-[11px] text-muted-foreground">
            v{evidence.previous.versionNumber} · {formatDateTime(evidence.previous.crawledAt)}
            <Icon name="arrow-right-01" size={11} className="mx-1.5 inline-block align-[-2px]" />
            v{evidence.current.versionNumber} · {formatDateTime(evidence.current.crawledAt)}
          </p>
        )}
      </div>

      {fields.length > 0 && (
        <DataTable
          columns={fieldColumns}
          groups={CATEGORY_ORDER
            .map((category) => ({
              id: category,
              header: <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{CATEGORY_LABELS[category]}</span>,
              rows: fieldRows.filter((row) => row.category === category),
            }))
            .filter((group) => group.rows.length > 0)}
          rowKey={(row) => row.id}
          minWidth={680}
          verticalDividers
        />
      )}

      {lines.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Text changes</p>
          <DataTable columns={lineColumns} rows={lineRows} rowKey={(row) => row.id} minWidth={560} verticalDividers />
          <div>
            {lines.length > 12 && (
              <button
                onClick={() => setShowAllLines((v) => !v)}
                className="mt-2 px-2 font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                {showAllLines ? "Show fewer" : `Show all ${lines.length} lines${evidence.truncated ? " (capped at 200 each)" : ""}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
